import { useTranslation } from 'react-i18next';
import type { DisplayPreviewSlide } from '../DisplayPreviewCarousel';

type DisplayNowPlayingProps = {
  mode: 'standard' | 'direct' | 'draft-standard';
  playlistName: string;
  slides: DisplayPreviewSlide[];
  busy?: boolean;
  removingItemId?: string | null;
  onAdd: () => void;
  onRemoveAssignment: () => void;
  onRemoveDirectItem: (itemId: string) => void;
};

function Thumb({
  slide,
  index,
  removable,
  removing,
  busy,
  onRemove,
}: {
  slide: DisplayPreviewSlide;
  index: number;
  removable: boolean;
  removing: boolean;
  busy: boolean;
  onRemove: (itemId: string) => void;
}) {
  const { t } = useTranslation('displays');
  return (
    <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-[8px] border border-line bg-black">
      {slide.kind === 'video' ? (
        <video src={slide.src} className="h-full w-full object-cover" muted playsInline aria-hidden />
      ) : (
        <img src={slide.src} alt="" className="h-full w-full object-cover" draggable={false} />
      )}
      {removable && slide.itemId ? (
        <button
          type="button"
          title={t('nowPlaying.removeItem', { index: index + 1 })}
          aria-label={t('nowPlaying.removeItem', { index: index + 1 })}
          disabled={busy}
          onClick={() => onRemove(slide.itemId!)}
          className="absolute end-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-black/70 text-xs font-bold leading-none text-white hover:bg-black disabled:opacity-50"
        >
          {removing ? '…' : '×'}
        </button>
      ) : null}
    </div>
  );
}

function ThumbRow({
  slides,
  removable,
  busy,
  removingItemId,
  onRemove,
}: {
  slides: DisplayPreviewSlide[];
  removable: boolean;
  busy: boolean;
  removingItemId: string | null;
  onRemove: (itemId: string) => void;
}) {
  const { t } = useTranslation('displays');
  const visible = slides.slice(0, 6);
  const overflow = slides.length - visible.length;

  if (slides.length === 0) {
    return <p className="m-0 text-xs text-muted">{t('nowPlaying.noPreviewImages')}</p>;
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto">
      {visible.map((slide, index) => (
        <Thumb
          key={slide.id}
          slide={slide}
          index={index}
          removable={removable}
          removing={slide.itemId != null && slide.itemId === removingItemId}
          busy={busy}
          onRemove={onRemove}
        />
      ))}
      {overflow > 0 ? (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[8px] border border-dashed border-line text-xs font-semibold text-muted">
          {t('nowPlaying.overflow', { count: overflow })}
        </div>
      ) : null}
    </div>
  );
}

function PlusMinus({
  busy,
  onAdd,
  onRemove,
}: {
  busy: boolean;
  onAdd: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('displays');
  return (
    <div className="flex shrink-0 gap-1.5">
      <button
        type="button"
        title={t('nowPlaying.addContent')}
        aria-label={t('nowPlaying.addContent')}
        disabled={busy}
        onClick={onAdd}
        className="grid h-9 w-9 place-items-center rounded-[10px] border border-line text-lg font-bold leading-none text-ink hover:border-[var(--gs-accent)] disabled:opacity-50"
      >
        +
      </button>
      <button
        type="button"
        title={t('nowPlaying.removeContent')}
        aria-label={t('nowPlaying.removeContent')}
        disabled={busy}
        onClick={onRemove}
        className="grid h-9 w-9 place-items-center rounded-[10px] border border-line text-lg font-bold leading-none text-ink hover:border-[var(--gs-accent)] disabled:opacity-50"
      >
        −
      </button>
    </div>
  );
}

/**
 * Playlist: same idea as the Playlists list row — long bar with name + thumbs beside it + +/-.
 * Direct: individual thumbs with × so one image can be removed.
 */
export function DisplayNowPlaying({
  mode,
  playlistName,
  slides,
  busy = false,
  removingItemId = null,
  onAdd,
  onRemoveAssignment,
  onRemoveDirectItem,
}: DisplayNowPlayingProps) {
  const { t } = useTranslation('displays');
  const isDirect = mode === 'direct';
  const itemLabel =
    slides.length === 0 ? t('nowPlaying.empty') : t('nowPlaying.items', { count: slides.length });

  if (isDirect) {
    return (
      <div className="flex items-center gap-2" data-testid="display-now-playing">
        <div className="min-w-0 flex-1">
          <ThumbRow
            slides={slides}
            removable
            busy={busy}
            removingItemId={removingItemId}
            onRemove={onRemoveDirectItem}
          />
        </div>
        <PlusMinus busy={busy} onAdd={onAdd} onRemove={onRemoveAssignment} />
      </div>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[12px] border border-line px-4 py-3"
      style={{ background: 'var(--gs-panel)' }}
      data-testid="display-now-playing"
    >
      <span
        className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-[10px] border"
        style={{
          background: 'var(--gs-panel2)',
          borderColor: 'var(--gs-line)',
          color: 'var(--gs-accent)',
        }}
        aria-hidden
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M4 6h11M4 12h11M4 18h7" />
          <path d="m17 12 5 3-5 3z" fill="currentColor" stroke="none" />
        </svg>
      </span>

      <span className="flex min-w-[9rem] flex-1 flex-col gap-0.5">
        <span className="truncate text-[15px] font-bold text-ink">{playlistName}</span>
        <span className="text-[12.5px] text-muted">
          {itemLabel}
          {mode === 'draft-standard' ? ` · ${t('nowPlaying.notSavedYet')}` : ''}
        </span>
      </span>

      <div className="min-w-0 flex-[1.4]">
        <ThumbRow
          slides={slides}
          removable={false}
          busy={busy}
          removingItemId={null}
          onRemove={onRemoveDirectItem}
        />
      </div>

      <PlusMinus busy={busy} onAdd={onAdd} onRemove={onRemoveAssignment} />
    </div>
  );
}
